import { useEffect, useMemo, useState } from 'react';
import { Coins, Layers, PiggyBank, TrendingUp } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import type { Address } from 'viem';
import { useAccount, useReadContract, useReadContracts, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { EARN_ABI, ERC20_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { logTransaction } from '../web3/logTransaction';
import { useTokenPrices } from '../web3/usePrices';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

const BLOCKS_PER_YEAR = 10_512_000;
const toNum = (v: bigint) => Number(formatUnits(v, 18));

function fmt(value: bigint | undefined, digits = 4) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, 18)).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function fmtApr(apr: number | undefined) {
  if (apr === undefined) return '-';
  if (apr > 999) return '999%+';
  return `${apr.toFixed(1)}%`;
}

function EarnStat({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Layers;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className={`text-base font-semibold mt-1 tabular-nums truncate ${accent ? 'text-tide-400' : 'text-foam-100'}`}>
        {value}
      </div>
    </div>
  );
}

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, 18);
  } catch {
    return null;
  }
}

export default function EarnView() {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const earn = deployment?.earn;

  const prices = useTokenPrices();

  const { data: budget } = useReadContract({
    address: earn?.address,
    abi: EARN_ABI,
    functionName: 'rewardBudget',
    chainId: readChainId,
    query: { enabled: Boolean(earn), refetchInterval: 15000 },
  });

  const poolEntries = useMemo(() => Object.entries(earn?.pools ?? {}), [earn]);

  const { data: poolInfos } = useReadContracts({
    contracts: poolEntries.map(
      ([, pid]) =>
        ({
          address: earn?.address,
          abi: EARN_ABI,
          functionName: 'poolInfo',
          args: [BigInt(pid)],
          chainId: readChainId,
        }) as const,
    ),
    query: { enabled: Boolean(earn) && poolEntries.length > 0, refetchInterval: 15000 },
  });

  const stats = useMemo(() => {
    const aprBySymbol = new Map<string, number>();
    let totalTvl = 0;
    const plgPrice = prices.priceOf('PLG') ?? 0;
    poolEntries.forEach(([symbol], i) => {
      const info = poolInfos?.[i];
      if (info?.status !== 'success') return;
      const r = info.result as readonly [string, bigint, bigint, bigint, bigint];
      const rewardPerBlock = toNum(r[1]);
      const totalStaked = toNum(r[4]);
      const tokenPrice = prices.priceOf(symbol);
      if (tokenPrice !== undefined) totalTvl += totalStaked * tokenPrice;
      if (tokenPrice !== undefined && totalStaked > 0) {
        aprBySymbol.set(symbol, ((rewardPerBlock * BLOCKS_PER_YEAR * plgPrice) / (totalStaked * tokenPrice)) * 100);
      }
    });
    const aprs = [...aprBySymbol.values()];
    return { totalTvl, aprBySymbol, topApr: aprs.length ? Math.max(...aprs) : undefined };
  }, [poolInfos, poolEntries, prices]);

  if (!earn) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">
        Earn is not deployed on this network.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <PiggyBank size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Earn</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            단일 자산을 예치하고 블록마다 PLG 보상을 받는 캠페인형 예치 상품
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <EarnStat
          icon={PiggyBank}
          label="전체 예치 (TVL)"
          value={prices.ready ? `$${stats.totalTvl.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '-'}
        />
        <EarnStat icon={Layers} label="예치 상품" value={String(poolEntries.length)} />
        <EarnStat
          icon={Coins}
          label="남은 보상 재원"
          value={`${fmt(budget, 2)} PLG`}
        />
        <EarnStat icon={TrendingUp} label="최고 APR" value={fmtApr(stats.topApr)} accent />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {poolEntries.map(([symbol, pid]) => (
          <EarnCard key={symbol} symbol={symbol} pid={pid} earnAddress={earn.address} apr={stats.aprBySymbol.get(symbol)} />
        ))}
      </div>

      <TrustBar contract={earn.address} badges={['nonCustodial', 'principalSafe', 'onchain']} note="예치 원금은 보상 재원과 무관하게 언제든 출금할 수 있으며, 보상은 사전 적립된 PLG 재원 한도 내에서만 지급됩니다. (BSC 테스트넷)" />
    </div>
  );
}

function EarnCard({
  symbol,
  pid,
  earnAddress,
  apr,
}: {
  symbol: string;
  pid: number;
  earnAddress: Address;
  apr?: number;
}) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const tokenAddress = symbol === 'SALT' ? deployment?.saltRwa?.token : deployment?.tokens[symbol];

  const [mode, setMode] = useState<'stake' | 'unstake'>('stake');
  const [amount, setAmount] = useState('');
  const [txError, setTxError] = useState<string | null>(null);

  const amountWei = safeParse(amount);

  const { data: poolInfo } = useReadContract({
    address: earnAddress,
    abi: EARN_ABI,
    functionName: 'poolInfo',
    args: [BigInt(pid)],
    chainId: readChainId,
    query: { refetchInterval: 15000 },
  });
  const totalStaked = poolInfo?.[4];
  const rewardPerBlock = poolInfo?.[1];

  const { data: walletBalance, refetch: refetchWallet } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(tokenAddress && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, earnAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(tokenAddress && address) },
  });

  const { data: userInfo, refetch: refetchUser } = useReadContract({
    address: earnAddress,
    abi: EARN_ABI,
    functionName: 'userInfo',
    args: address ? [BigInt(pid), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address) },
  });
  const staked = userInfo?.[0];

  const { data: pending, refetch: refetchPending } = useReadContract({
    address: earnAddress,
    abi: EARN_ABI,
    functionName: 'pendingReward',
    args: address ? [BigInt(pid), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address), refetchInterval: 5000 },
  });

  const refetchAll = () => {
    refetchWallet();
    refetchAllowance();
    refetchUser();
    refetchPending();
  };

  const needsApproval =
    mode === 'stake' && amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract: writeAction, data: actionHash, isPending: isActionPending, reset: resetAction } = useWriteContract();
  const { isLoading: isActionConfirming, isSuccess: isActionConfirmed, error: actionReceiptError, refetch: retryActionReceipt } = useWaitForTransactionReceipt({
    hash: actionHash,
    timeout: 300_000,
  });

  const {
    writeContract: writeHarvest,
    data: harvestHash,
    isPending: isHarvestPending,
    reset: resetHarvest,
  } = useWriteContract();
  const { isLoading: isHarvestConfirming, isSuccess: isHarvestConfirmed, error: harvestReceiptError, refetch: retryHarvestReceipt } = useWaitForTransactionReceipt({
    hash: harvestHash,
    timeout: 300_000,
  });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApproveConfirmed]);

  useEffect(() => {
    if (isActionConfirmed && actionHash && address) {
      logTransaction({
        type: mode === 'stake' ? 'earnDeposit' : 'earnWithdraw',
        hash: actionHash,
        account: address,
        chainId: readChainId,
        details: { pool: `EARN-${symbol}`, amount },
      });
      setAmount('');
      refetchAll();
      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActionConfirmed]);

  useEffect(() => {
    if (isHarvestConfirmed && harvestHash && address) {
      logTransaction({
        type: 'harvest',
        hash: harvestHash,
        account: address,
        chainId: readChainId,
        details: { pool: `EARN-${symbol}` },
      });
      refetchAll();
      resetHarvest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHarvestConfirmed]);

  const handleApprove = () => {
    if (!tokenAddress) return;
    setTxError(null);
    writeApprove(
      { address: tokenAddress, abi: ERC20_ABI, functionName: 'approve', args: [earnAddress, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleAction = () => {
    if (!amountWei) return;
    setTxError(null);
    writeAction(
      {
        address: earnAddress,
        abi: EARN_ABI,
        functionName: mode === 'stake' ? 'deposit' : 'withdraw',
        args: [BigInt(pid), amountWei],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleHarvest = () => {
    setTxError(null);
    writeHarvest(
      { address: earnAddress, abi: EARN_ABI, functionName: 'deposit', args: [BigInt(pid), 0n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const isPending =
    isApprovePending ||
    isApproveConfirming ||
    isActionPending ||
    isActionConfirming ||
    isHarvestPending ||
    isHarvestConfirming;

  const maxAmount = mode === 'stake' ? walletBalance : staked;

  let buttonLabel = '수량을 입력하세요';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = '지갑을 연결하세요';
  } else if (!onCorrectChain) {
    buttonLabel = '네트워크를 전환하세요';
  } else if (amountWei) {
    if (needsApproval) {
      buttonLabel = `Approve ${symbol}`;
      buttonAction = handleApprove;
      buttonDisabled = isPending;
    } else {
      buttonLabel = mode === 'stake' ? '예치' : '출금';
      buttonAction = handleAction;
      buttonDisabled = isPending;
    }
  }

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TokenIcon symbol={symbol} size={28} />
          <div>
            <div className="text-sm font-semibold text-foam-100">{symbol} 예치</div>
            <div className="text-[11px] text-foam-500">보상: {fmt(rewardPerBlock, 4)} PLG/블록</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-foam-500">APR</div>
          <div className="text-base font-semibold text-tide-400 tabular-nums">{fmtApr(apr)}</div>
        </div>
      </div>

      <div className="flex justify-between text-[11px] text-foam-500 px-1">
        <span>총 예치 (TVL)</span>
        <span className="text-foam-300 tabular-nums">
          {fmt(totalStaked, 2)} {symbol}
        </span>
      </div>

      <div className="p-3 rounded-lg border border-tide-400/30 bg-tide-400/5 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-foam-500 flex items-center gap-1.5">
            미수령 PLG
            {pending !== undefined && pending > 0n && <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />}
          </div>
          <div className="text-lg font-semibold text-tide-400 tabular-nums">{fmt(pending, 6)}</div>
        </div>
        <button
          onClick={handleHarvest}
          disabled={!pending || pending === 0n || isPending}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed transition-colors"
        >
          수령
        </button>
      </div>

      <div className="flex gap-1 p-0.5 rounded-lg bg-abyss-900 border border-abyss-700 w-fit">
        {(['stake', 'unstake'] as const).map((m) => (
          <button
            key={m}
            onClick={() => {
              setMode(m);
              setAmount('');
              setTxError(null);
            }}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              mode === m ? 'bg-tide-500 text-abyss-950' : 'text-foam-300 hover:text-foam-100'
            }`}
          >
            {m === 'stake' ? '예치' : '출금'}
          </button>
        ))}
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/60">
        <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
          <span>{mode === 'stake' ? '지갑 잔액' : '예치 중'}</span>
          <button
            onClick={() => maxAmount && setAmount(formatUnits(maxAmount, 18))}
            className="text-tide-400 hover:text-tide-300 font-medium"
          >
            MAX: {fmt(maxAmount, 4)}
          </button>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
      </div>

      <button
        onClick={buttonAction}
        disabled={buttonDisabled || isPending}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {isPending ? '처리 중...' : buttonLabel}
      </button>

      {txError && (
        <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: actionReceiptError, hash: actionHash, retry: retryActionReceipt },
        { error: harvestReceiptError, hash: harvestHash, retry: retryHarvestReceipt },
      ]} />
      {isActionConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
          {mode === 'stake' ? '예치' : '출금'}가 완료되었습니다.
        </div>
      )}
      {isHarvestConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">보상을 수령했습니다.</div>
      )}
    </div>
  );
}
